<template>
    <div class = "columns is-centered login-container ">
        <div class="column is-12-mobile is-8">
			<BaseLoader class="login-loader" v-if="showLoader" />
            <div v-if="!flow.smsSent && !loggedIn && !skipToPin">
                <div class="has-text-centered">
                    <p class="is-size-1">😊</p>
                </div>
                <div class="field">
                     <label class="label is-size-4 heading-txt"><b>Login/Sign Up with your Mobile Number</b></label>
                </div>
                <div>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <input 
                                :class="(liveValidate) ? 'is-success' : 'is-danger'"
                                v-model="phone" 
                                class="input input--login" 
                                type="tel" 
                                placeholder="(607) 123 4567"
								aria-label="Enter your mobile phone number here."
                                @keyup.enter="submitRequest"
                                v-focus />
                        </div>
                    </div>
                </div>

                <div class="control">
                    <BaseButton title="Send Pin"
                        :classNames="['button--modifier']"
                        @click.native="submitRequest"
                        />
                    <p class="is-size-7 has-text-grey-light">
                        By clicking on Send Pin, you agree to our
                        <u>
                            <a class="has-text-grey-light" target="_blank" href="https://www.touchpoint.io/privacy.php">
                                Terms of Service
                            </a>
                        </u>
                        and
                        <u>
                            <a class="has-text-grey-light" target="_blank" href="https://www.touchpoint.io/privacy.php">
                                Privacy Policy
                            </a>
                        </u>
                    </p>
                </div>
            </div>
            <div v-if="flow.smsSent && !loggedIn">
                <div class="has-text-centered">
                    <p class="is-size-1">💬</p>
                </div>

                <div class="field">
                     <label class="label is-size-4 heading-txt"><b>Thanks! <br> Enter the PIN we just sent</b></label>
                </div>

                <div class="field half-width">
                    <div class="control">
                        <input v-model="login.token" 
                               class="input base-input-border"
                               type="tel" 
                               placeholder="00000"
							   aria-label="Enter the pin that was sent to your phone here."
                               @keyup.enter="submitRequest"
                               v-focus />
                    </div>
                </div>
                <div class="control">
                    <BaseButton title="Verify"
                        :classNames="['button--modifier']"
                        @click.native="submitRequest" />
					<br/>
					<p class="tp-link" aria-label="click here to receive a voice call with your pin number" @click="handleSendVoicePin">
						Trouble receiving PIN?<br/>
						Click <span class="underline-link">here</span> to get your PIN in a call.
					</p>
					<p class="is-size-7 has-text-grey-light">
						If you still can not receive the SMS code, please call 
						<a :href="`tel:${location.contactPhone}`">{{formatPhone(location.contactPhone)}}</a> to place your order.
					</p>
                </div>
            </div>
             <p :class="messageClass">{{msg}}</p>
        </div>
    </div>
</template>

<script>
import * as moment from "moment";

import Analytics from '@Analytics';
import { ACCOUNT, ACCOUNT_CHECKOUT, LOCATION, RECENT_ORDERS, MENU_ITEMS, FRANCHISOR } from "@/stores/defs";
import { base } from "@/services/app.service";
import { mapGetters, mapActions } from "vuex";
import { LOGIN_ANALYTIC_EVENT } from "@/common/analytic-events/loginAnalyticEvent";
import { hasNewCustomerName } from '@/utils/helper.functions';
import localStorageAdapter from '@/utils/LocalStorageAdapter';

// TODO use country codes
// countryCodes: Object.keys(metadata.countries),
// import metadata from "libphonenumber-js/metadata.full.json";
import LinkUser from '@/components/auth/LinkUser';
import LocationModel from '@/models/LocationModel';

import phoneValidatorMixin from '@/components/mixins/phoneValidatorMixin';
import storeLoaderMixin from '@/components/mixins/storeLoaderMixin';
import modalMixin from '@/components/mixins/modalMixin';

import customerService from "@/services/customer.service";
import guestService from "@/services/guest.service";
import locationService from '@/services/location.service';
import recentOrderService from "@/services/recent.orders.service";

import DateManipulator from '@/utils/DateManipulator';

export default {
	mixins: [
		phoneValidatorMixin,
		storeLoaderMixin,
		modalMixin
	],
    props: {
        routeTo: {
            type: String,
            default: window.location.pathname || "/",
        },
        loginNoStore: {
            type: Boolean,
            default: false
		},
		skipToPin: {
			type: Boolean,
			default: false
		}
    },
    computed: {
        ...mapGetters({
            customer: ACCOUNT.GET_CUSTOMER,
            location: LOCATION.GET_LOCATION,
            loggedIn: ACCOUNT.GET_LOGGED_IN,
			guestAddress: ACCOUNT.GET_GUEST_ADDRESS,
			guestPhone: ACCOUNT.GET_GUEST_PHONE,
			guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
            hasMenuItems: MENU_ITEMS.HAS_ITEMS,
            franchisor: FRANCHISOR.GET_FRANCHISOR
		}),
		showLoader() {
			if (this.customer && this.customer.id) {
				return false;
			}
			return !this.flow.smsSent && this.liveValidate && this.skipToPin;
		},
    },
    data() {
        return {
            msg: "",
            messageClass: '',
            login: {
                countryCode: "US",
                formattedNumber: "",
                phone: null,
                token: null,
                signup: false
            },
            flow: {
                smsSent: false
            }
        };
    },
    mounted() {
		this.$tpa.trackLoginPage();
		if (this.loggedIn) {
			this.$router.push({ path: this.routeTo });
            this.$modal.hide('loginModal');
		}
		if (this.guestPhone.length > 1) {
			this.phone = this.guestPhone;
			this.submitRequest();
		}
    },
    methods: {
        ...mapActions({
            setLoggedIn: ACCOUNT.SET_LOGGED_IN,
			setCustomer: ACCOUNT.SET_CUSTOMER,
			updateCustomer: ACCOUNT.UPDATE_CUSTOMER,
			setGuestId: ACCOUNT_CHECKOUT.SET_GUEST_ID,
            setRecentOrders: RECENT_ORDERS.SET_RECENT_ORDERS,
            setActiveOrder: RECENT_ORDERS.SET_ACTIVE_ORDER,
            setLocation: LOCATION.SET_LOCATION,
            setOffers: ACCOUNT.SET_OFFERS
        }),
		handleNewCustomerName() {
			// TODO remove this hacky bit should not look for specific strings
			// or should be part of a configuration
			const customer = this.customer;
			let needsNameFlag = false;
			if (hasNewCustomerName(customer.firstName, customer.lastName)) {
				needsNameFlag = true;
				this.updateCustomer({
					firstName: null,
					lastName: null,
				});
			}
		},
		handleSendVoicePin() {
			customerService.sendVoicePin();
		},
		// TODO may want this in a mixin
		trackLoginFailure(phoneNumber) {
			this.$tpa.trackLoginFailurePage();
			this.$tpa.trackLoginFailure(phoneNumber);
		},
        getLastLocationVisited(customer) {
            if (customer && customer.loyaltyInfo.length && customer.loyaltyInfo[0].points && customer.loyaltyInfo[0].points.locationsVisited) {
                const locationsVisited = customer.loyaltyInfo[0].points.locationsVisited;
                let lastLocationVisited = {
                    id: null,
                    date: null
                };
                for (let key in locationsVisited) {
                    if (!lastLocationVisited.id) {
                        lastLocationVisited = {
                            id: key,
                            date: locationsVisited[key]
                        }
                    } else if (lastLocationVisited.date < lastLocationVisited[key]) {
                        lastLocationsVisited = {
                            id: key,
                            date: locationsVisited[key]
                        }
                    }
                }
                return lastLocationVisited.id;
            } else {
                return false;
            }
		},
		logoutGuest() {
			const guestId = this.guestId;
			const location = this.location;
			if (guestId) {
				guestService.releaseGuestCustomer({
					base: location.id,
					customerId: guestId,
				}, ({released, customerId}) => {
					if (released) {
						this.setGuestId(null);
					}
				});
			}
		},
        submitRequest() {
            let cmd = null;
            
            if (this.liveValidate) {
                this.msg = "";
                if (this.phone && this.login.token) {
                    cmd = "verify_token";
                } else if (this.phone && !this.login.token) {
                    cmd = "generate_token";
                }

                if ((!this.phone && !this.login.token) || !this.phone) {
                    this.msg = "Please fill out the required fields";
                } else {
                    const loginReq = {
                        lb: this.location.id || this.franchisor.id,
                        cmd: cmd,
                        phone: this.phone,
                        token: this.login.token
					};
                    customerService
                        .login(loginReq)
                        .then(res => {
                            if (res.data.message === "Successfully sent PIN") {
								this.phone = res.data.body.phone;
								// TODO fix 'new_acount' spelling error (fix needed server side too)
                                this.login.signup = !!res.data.body.new_acount;
								this.flow.smsSent = true;
								this.$tpa.trackLoginPinPage();
                            } else if (res.data.message === "Successfully logged in") {
								this.flow.smsSent = false;
								this.msg = "You've successfully been logged in.";
                                this.messageClass = "login-message";
								this.$tpa.trackLoginSuccessPage();
                                // TODO move this request and callback into a service
                                const rawData = res.data.body.customer.customer;
                                const customer = customerService.getCustomerOnLogin(rawData);

								this.setCustomer(customer);
								this.setLoggedIn(true);
								this.logoutGuest();
								this.$tpa.trackLogin(customer.id);

								localStorageAdapter.setCustomerBase(customer.id);
                                customerService.setCustomerForStorage(customer);
                            
                                const offers = customerService.convertAllCustomerOffers(customer, this.franchisor, customer.offers);
                                this.setOffers(offers);
    
                                const handleFlow = () => {
                                    setTimeout(() => {
                                        if (this.login.signup || hasNewCustomerName(this.customer.firstName, this.customer.lastName)) {
											this.$modal.hide("loginModal");
											this.handleNewCustomerName();
                                            setTimeout(() => {
                                                this.triggerModal({
                                                    classes: 'global-modal',
                                                    component: LinkUser,
                                                    modalName: 'contentModal',
                                                    showCloseButton: false,
                                                    title: "Welcome New Guest!",
                                                    contentProperties: {
                                                        routeTo: this.routeTo
                                                    }
                                                })
                                            }, 200);
                                        } else {
                                            return recentOrderService
                                                .getRecentOrders(customer.id, this.location.id)
                                                .then(res => {
                                                    if (res.data.body.recent_scripts) {
                                                        this.setRecentOrders(res.data.body.recent_scripts);
                                                    }
													// TODO needs clean up, should be talking to an ActiveOrder object
                                                    if (res.data.body.active_order && this.location) {
                                                        if (res.data.body.active_order && this.location && 
                                                        DateManipulator.isMomentToday(moment(new Date(res.data.body.active_order.order.promise_date * 1000), this.location.timeZone))) {
                                                            this.setActiveOrder(recentOrderService.convertSingleOrder(res.data.body.active_order));
                                                        } else {
                                                            this.setActiveOrder(null);
                                                        }
													}
                                                    let query = {};
                                                    if (this.$route.query) {
                                                        query = this.$route.query;
													}
													if (this.$route.name === 'checkout') {
														this.routeTo = '/checkout';
													}
                                                    this.$router.push({ path: this.routeTo, query });
													this.$modal.hide("loginModal");
                                                })
                                                .catch(err => {
                                                    console.log(err);
													this.$modal.hide("loginModal");
                                                    this.$router.push({ path: this.routeTo });
                                                });
                                        }
                                    }, 1000);
                                };
                                // logging in with no store
                                if (this.loginNoStore) {
									const lb = this.getLastLocationVisited(customer);
                                    if (lb) {
                                        locationService.getSingleLocation(lb)
										.then((res) => {
											const locationModel = new LocationModel(res);
											if (locationModel.onlineStoreOpen) {
												this.setLocation(locationModel);
												this.loadAll(locationModel.id);
												this.$modal.hide("loginModal");
												window.location.reload();
											} else {
												this.$router.push({ path: this.routeTo });
											}
										})
										.catch((err) => {
											console.log(err);
										});
                                    }
                                } else {
                                    handleFlow()
                                }
                            } else {
								this.trackLoginFailure(loginReq.phone);
                                this.msg = res.data.message;
                            }

                        })
                        .catch(err => {
							this.trackLoginFailure(loginReq.phone);
                            console.log(err);
                            this.msg = err.message;
                        });
                }
            } else {
                this.msg = "Please enter a valid phone number";
            }
        }
    },
};
</script>

<style lang="scss" scoped>
@import "globalStyles";

.login-container {
    padding: 1em 0.5em;
    .field {
        margin: auto;

        width: 100%;
    }
    label {
        text-align: center;
        &.label {
            font-weight: normal;
        }
    }
    .control {
        margin: 0;
        margin-bottom: 2rem;

		text-align: center;
		p {
			padding: 0.5em 0;
		}
	}
	
	.login-message {
		font-size: 2rem;
	}
	.input--login {
		max-width: 20em;
	}
}

@include desktop {
    .login-container {
        min-height: 40vh;
        align-items: center;
    }
}
@include mobile {
    .login-container {
        .control {
            margin: 0;
            margin-bottom: 2rem;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/auth/Login.vue